import React from "react";
import dynamic from "next/dynamic";
import { getInitialData } from "../../service/apiFetch";
import NotFound from "../../components/ErrorPage/NotFound";
import WsrvImage from "../../components/utils/WsrvImage";
import Main from "../../components/Pageblock/Main";
import PhotoMain from "../../components/Photo/PhotoMain";
import VideoMain from "../../components/Video/VideoMain";
import DownlMain from "../../components/Download/DownlMain";
import FaqMain from "../../components/Faq/FaqMain";

// Home page components
import MainSection from "../../components/Home/MainSection";
import TestseriesSection from "../../components/Home/TestseriesSection";
import ClcGuide from "../../components/Home/ClcGuide";
import KeyFeatures from "../../components/Home/KeyFeatures";
import ViedeoSection from "../../components/Home/ViedeoSection";
import BookSlider from "../../components/Home/BookSlider";
import BookSection from "../../components/Home/BookSection";
import Faq from "../../components/Home/Faq";
import Download from "../../components/Home/Download";

// Lazy-load heavy components only when their shortCode appears
const SliderMain = dynamic(() => import("../../components/Slider/SliderMain"));
const TestMain = dynamic(() => import("../../components/Testimonial/TestMain"));
const CallbackSection = dynamic(() => import("../../components/Home/CallbackSection"), { ssr: false });

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps = async (context) => {
  const uri = context.params?.url1 || [];
  const url = uri.length > 0 ? uri.join("/") : "__home";

  const initialData = await getInitialData(url);

  // Pass all fields as top-level props so _app.js can use them
  // for header, footer, MetaTags, etc.
  return {
    props: {
      websiteData: initialData?.websiteData || "",
      data: initialData?.data || "",
      title: initialData?.title || "",
      metaTags: initialData?.metaTags || "",
      url: initialData?.url || "",
    },
  };
};

const index = ({ data, websiteData, title, metaTags }) => {
  if (data?.error) {
    return (
      <NotFound
        errorCode={data?.details?.statusCode}
        errorType={data?.details?.error?.message}
      />
    );
  }

  const pageData = data?.pageData || {};
  const contentBlock = data?.contentBlock || [];

  if (!contentBlock?.length && !pageData) {
    return null;
  }

  return (
    <div>
      {/* Feature image banner - LCP optimized via wsrv */}
      {pageData?.featureImg && (
        <div style={{ maxHeight: "400px", overflow: "hidden" }}>
          <WsrvImage
            src={pageData.featureImg}
            alt={pageData?.title || title || "Page"}
            priority={true}
            preset="hero"
            width={1920}
            height={400}
            sizes="100vw"
            style={{ width: "100%", maxHeight: "400px", objectFit: "cover" }}
          />
        </div>
      )}

      {contentBlock.length&&contentBlock?.map((item, index) => (
        <div
          key={item._id || index}
          className={`${index % 2 === 0 ? "bg-[#f0fdf4]" : ""}`}
        >
          {/* Always render the Main heading/content block */}
          {/* <div id={item.shortCode}>
            <Main basicData={item} />
          </div> */}

          {/* ShortCode-based rendering (Home page sections) */}
          {item?.shortCode === "MAIN_SECTION" && <MainSection data={item} />}
          {item?.shortCode === "TESTSERIES" && (
            <TestseriesSection
              data={item}
              centers={websiteData?.shortCodes?.OFFLINE_CENTER}
            />
          )}
          {item?.shortCode === "CLC_GUIDING" && <ClcGuide data={item} />}
          {item?.shortCode === "KEY_FEATURES" && <KeyFeatures data={item} />}
          {item?.shortCode === "VIDEO_SECTION" && <ViedeoSection data={item} />}
          {item?.shortCode === "BOOKS_LIST" && <BookSlider data={item} />}
          {item?.shortCode === "BOOK_SECTION" && <BookSection data={item} />}
          {item?.shortCode === "REQUEST_CALL" && (
            <CallbackSection data={item} />
          )}
          {item?.shortCode === "FAQ" && <Faq data={item} />}
          {item?.shortCode === "DOWNLOAD" && <Download data={item} />}

          {/* Type-based rendering (dynamic page content blocks) */}
          {item?.advanceData?.type === "PHOTO" && <PhotoMain data={item} />}
          {item?.advanceData?.type === "VIDEO" && <VideoMain data={item} />}
          {item?.advanceData?.type === "DOWNLOAD" && <DownlMain data={item} />}
          {item?.advanceData?.type === "SLIDER" && <SliderMain data={item} />}
          {item?.advanceData?.type === "FAQ" && <FaqMain data={item} />}
          {item?.advanceData?.type === "TESTIMONIAL" && <TestMain data={item} />}
        </div>
      ))}
    </div>
  );
};

export default index;
